import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import { WaitlistModal } from './WaitlistModal';

interface CardData {
  id: number;
  type: 'agent' | 'writer' | 'reader';
  label: string;
  findText: string;
  imagePath: string;
}

const cards: CardData[] = [
  {
    id: 0,
    type: 'agent',
    label: 'Agent Dashboard',
    findText: 'writers',
    imagePath: '/agent-dashboard.png',
  },
  {
    id: 1,
    type: 'writer',
    label: 'Writer Dashboard',
    findText: 'agents',
    imagePath: '/writer-dashboard.png',
  },
  {
    id: 2,
    type: 'writer',
    label: 'Writer Dashboard',
    findText: 'readers',
    imagePath: '/writer-dashboard.png',
  },
  {
    id: 3,
    type: 'reader',
    label: 'Reader Dashboard',
    findText: 'manuscripts',
    imagePath: '/reader-dashboard.png',
  },
];

export const AnimatedHero: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [displayText, setDisplayText] = useState(cards[0].findText);
  const [isPaused, setIsPaused] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const activeCard = cards[activeIndex];

  // Typing animation effect
  useEffect(() => {
    setIsTyping(true);
    setDisplayText('');
    let currentIndex = 0;
    const targetText = activeCard.findText;

    const typingInterval = setInterval(() => {
      if (currentIndex <= targetText.length) {
        setDisplayText(targetText.slice(0, currentIndex));
        currentIndex++;
      } else {
        setIsTyping(false);
        clearInterval(typingInterval);
      }
    }, 80); // Typing speed

    return () => clearInterval(typingInterval);
  }, [activeCard]);

  // Auto-rotation effect
  useEffect(() => {
    if (isPaused) return;

    const rotationInterval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % cards.length);
    }, 4000); // Rotate every 4 seconds

    return () => clearInterval(rotationInterval);
  }, [isPaused]);

  const handleCardClick = (index: number) => {
    if (index !== activeIndex) {
      setActiveIndex(index);
      setIsPaused(true);
      // Resume auto-rotation after 8 seconds
      setTimeout(() => setIsPaused(false), 8000);
    }
  };

  // Calculate card positions (stack effect)
  const getCardStyle = (index: number): React.CSSProperties => {
    const offset = (index - activeIndex + cards.length) % cards.length;
    const isHovered = hoveredIndex === index && offset > 0;

    return {
      position: 'absolute',
      width: '100%',
      height: '100%',
      borderRadius: theme.borderRadius.xlarge,
      border: `2px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.white,
      boxShadow: isHovered
        ? `0 20px 60px rgba(99, 102, 241, 0.25)`
        : '0 10px 40px rgba(0, 0, 0, 0.15)',
      transform: `
        translateX(${offset * 24}px)
        translateY(${offset * 24}px)
        scale(${1 - offset * 0.05})
        ${isHovered ? 'translateY(-8px)' : ''}
      `,
      zIndex: cards.length - offset,
      opacity: offset === 0 ? 1 : (isHovered ? 0.85 : 0.7),
      transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
      cursor: offset === 0 ? 'default' : 'pointer',
      overflow: 'hidden',
    };
  };

  return (
    <section className="animated-hero" style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '80px 40px',
      minHeight: '600px',
    }}>
      {/* Responsive CSS */}
      <style>{`
        @media (max-width: 768px) {
          .animated-hero {
            padding: 40px 20px !important;
            min-height: auto !important;
          }
          .hero-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
          .hero-badge {
            font-size: 12px !important;
            padding: 5px 12px !important;
          }
          .hero-title {
            font-size: 40px !important;
          }
          .hero-animated-text {
            min-width: 280px !important;
          }
          .hero-description {
            font-size: 18px !important;
          }
          .hero-button {
            padding: 12px 28px !important;
            font-size: 15px !important;
          }
          .hero-cards {
            aspect-ratio: 16 / 11 !important;
          }
        }
      `}</style>
      <div className="hero-grid" style={{
        display: 'grid',
        gridTemplateColumns: '1fr 2fr',
        gap: '80px',
        alignItems: 'center',
      }}>
        {/* Left side - Text */}
        <div>
          <div className="hero-badge" style={{
            display: 'inline-block',
            backgroundColor: theme.colors.primaryLight,
            color: theme.colors.primaryDark,
            padding: '6px 14px',
            borderRadius: theme.borderRadius.pill,
            fontSize: '13px',
            fontWeight: '600',
            marginBottom: '24px',
            letterSpacing: '0.02em',
          }}>
            In development
          </div>

          <h1 className="hero-title" style={{
            fontSize: '64px',
            fontWeight: '600',
            lineHeight: '1.1',
            marginBottom: '24px',
            color: theme.colors.textPrimary,
            letterSpacing: '-0.02em',
          }}>
            Find{' '}
            <span className="hero-animated-text" style={{
              color: theme.colors.primary,
              position: 'relative',
              display: 'inline-block',
              minWidth: '400px',
            }}>
              {displayText}
              {isTyping && (
                <span style={{
                  borderRight: `3px solid ${theme.colors.primary}`,
                  animation: 'blink 0.7s infinite',
                  marginLeft: '2px',
                }}>
                </span>
              )}
            </span>
          </h1>

          <p className="hero-description" style={{
            fontSize: '20px',
            lineHeight: '1.6',
            color: theme.colors.textSecondary,
            marginBottom: '40px',
          }}>
            We’re building a place for writers, beta readers and literary agents to connect.
          </p>

          <button
            className="hero-button"
            onClick={() => setIsModalOpen(true)}
            style={{
              backgroundColor: theme.colors.primary,
              color: 'white',
              border: 'none',
              padding: '14px 32px',
              borderRadius: theme.borderRadius.medium,
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            Join Waitlist
          </button>
        </div>

        {/* Right side - Rotating Cards */}
        <div className="hero-cards" style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 10',
        }}>
          {cards.map((card, index) => {
            const offset = (index - activeIndex + cards.length) % cards.length;

            return (
              <div
                key={card.id}
                style={getCardStyle(index)}
                onClick={() => handleCardClick(index)}
                onMouseEnter={() => offset > 0 && setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {/* Badge label */}
                <div style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  padding: '8px 16px',
                  borderRadius: theme.borderRadius.pill,
                  fontSize: '13px',
                  fontWeight: '600',
                  color: theme.colors.primary,
                  border: `1px solid ${theme.colors.border}`,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  zIndex: 10,
                }}>
                  {card.type === 'writer' ? 'Writer View' : card.type === 'agent' ? 'Agent View' : 'Reader View'}
                </div>

                <img
                  src={card.imagePath}
                  alt={card.label}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* CSS for blinking cursor */}
      <style>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `}</style>

      <WaitlistModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </section>
  );
};
